/**
 * Messages API helpers.
 */

export async function getConversations(includeClubs = false) {
  const url = includeClubs ? '/api/conversations?include=all' : '/api/conversations';
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch conversations');
  return res.json();
}

export async function getUnreadMessageConversationCount() {
  const res = await fetch('/api/conversations/unread-count', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch unread message count');
  const data = await res.json();
  return typeof data.count === 'number' ? data.count : 0;
}

export async function getConversation(conversationId) {
  const res = await fetch(`/api/conversations/${conversationId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch conversation');
  return res.json();
}

export async function getMessages(conversationId) {
  const res = await fetch(`/api/conversations/${conversationId}/messages`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch messages');
  return res.json();
}

export async function markConversationRead(conversationId) {
  const res = await fetch(`/api/conversations/${conversationId}/mark-read`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to mark conversation read');
  return res.json();
}

export async function createConversation(otherUserId) {
  const res = await fetch('/api/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ other_user_id: otherUserId }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create conversation');
  }
  return res.json();
}

export async function sendMessage(conversationId, content) {
  const res = await fetch(`/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to send message');
  }
  return res.json();
}

export async function createGroupConversation(participantIds) {
  const res = await fetch('/api/group-conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ participant_ids: participantIds }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to create group chat');
  }
  return res.json();
}

export async function getGroupConversation(groupConversationId) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch group conversation');
  return res.json();
}

export async function getGroupMessages(groupConversationId) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}/messages`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch group messages');
  return res.json();
}

export async function markGroupConversationRead(groupConversationId) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}/mark-read`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to mark group conversation read');
  return res.json();
}

export async function sendGroupMessage(groupConversationId, content) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to send message');
  }
  return res.json();
}

export async function addGroupParticipant(groupConversationId, userId) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}/participants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to add participant');
  }
  return res.json();
}

export async function updateGroupName(groupConversationId, name) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update group name');
  }
  return res.json();
}

export async function removeGroupParticipant(groupConversationId, userId) {
  const res = await fetch(`/api/group-conversations/${groupConversationId}/participants/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove participant');
  }
  return res.json();
}

